// © 2026 AttainIP Engineering

const ACCESS_TOKEN_KEY = 'admin_token';
const REFRESH_TOKEN_KEY = 'admin_refresh_token';

export function getToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setTokens(accessToken: string, refreshToken?: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem('oauth_state');
  localStorage.removeItem('oauth_code_verifier');
  localStorage.removeItem('oauth_redirect_uri');
}

function parseJwt(token: string): Record<string, unknown> | null {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
}

// The Attain DevOps admin console is platform_admin-ONLY (Attain operators,
// @attainip.ai). Enterprise firm admins (org_admin) are CUSTOMERS — they self-
// manage inside the enterprise product, never in this internal ops console. Do
// NOT re-add org_admin here: that would let a customer authenticate into the
// operator console (security boundary — RFC-0212 A2 correction).
const ADMIN_ROLES = ['platform_admin'];

export function isAuthenticated(): boolean {
  const token = getToken();
  if (!token) return false;

  const payload = parseJwt(token);
  if (!payload) { clearTokens(); return false; }

  const exp = payload.exp as number | undefined;
  if (exp && Date.now() / 1000 > exp) {
    clearTokens();
    return false;
  }

  const roles = payload.roles as string[] | undefined;
  return roles?.some((r) => ADMIN_ROLES.includes(r)) ?? false;
}

export interface CurrentUser {
  id: string;
  email: string;
  name: string;
  roles: string[];
  organizationId: string | null;
}

export function getCurrentUser(): CurrentUser | null {
  const token = getToken();
  if (!token) return null;

  const payload = parseJwt(token);
  if (!payload) return null;

  return {
    id: payload.sub as string,
    email: payload.email as string,
    name: (payload.name as string) || (payload.email as string),
    roles: (payload.roles as string[]) || [],
    organizationId: (payload.org_id as string) || null,
  };
}

export function isPlatformAdmin(user: Pick<CurrentUser, 'roles'> | null): boolean {
  return user?.roles.includes('platform_admin') ?? false;
}

export function isOrgAdmin(user: Pick<CurrentUser, 'roles'> | null): boolean {
  return user?.roles.includes('org_admin') ?? false;
}
